import { type InputRef, Table, Tag } from 'antd';
import { pick, sortBy, uniqWith, isNil } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type UserListDataFragment } from '../../../api/fragments/UserListData';
import { useListUsersQuery } from '../../../api/queries/listUsers';
import { useCompanies, useCompany } from '../../../components/contexts/CompanyContextManager';
import FilterBox from '../../../components/FilterBox';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import InternalErrorResult from '../../../components/results/InternalErrorResult';
import SearchBox from '../../../components/SearchBox';
import useSearchInDropDown from '../../../components/useSearchInDropDown';
import { dateFilterItems } from '../../../utilities/common';
import renderBooleanIcon from '../../../utilities/renderBooleanIcon';
import renderFilterIcon from '../../../utilities/renderFilterIcon';
import renderSearchIcon from '../../../utilities/renderSearchIcon';
import useActiveFilter from '../../../utilities/useActiveFilter';
import useFormatDate from '../../../utilities/useFormatDate';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../utilities/useSortAndFilterCache';
import useListReducer from './useListReducer';
import useSortOrder, { getSortField } from './useSortOrder';

const UserList = () => {
    const { t } = useTranslation(['userList', 'common']);

    const inputRef = useRef<InputRef>(null);

    const navigate = useNavigate();

    // get company context
    const companies = useCompanies();
    const company = useCompany(true);
    const formatDate = useFormatDate();

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.User);

    // get state from a reducer
    const [state, dispatch] = useListReducer(currentCache);

    // fetch data
    const { page, pageSize, sort, filter } = state;

    const companyFilter = useMemo(
        () => (companies.length <= 1 ? companies.map(company => company.id) : null),
        [companies]
    );

    const { data, loading, error } = useListUsersQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            filter: {
                ...filter,
                companyIds: filter?.companyIds?.length ? filter.companyIds : companyFilter,
            },
            sort: pick(['field', 'order'], sort),
        },
    });

    // prepare items as a data source
    const dataSource = useMemo(() => (data?.list?.items || []).map(item => ({ ...item, key: item.id })), [data]);
    const total = data?.list?.count || 0;
    const searchBox = useSearchInDropDown();

    const onChange = useCallback(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort':
                    return dispatch({
                        type: 'setSort',
                        sortBy: getSortField(sorter),
                    });

                case 'filter':
                    dispatch({ type: 'setPage', page: 1 });

                    return dispatch({
                        type: 'setFilter',
                        filterBy: {
                            companyIds: !filters.company ? companyFilter : (filters.company as string[]),
                            displayName: !filters.displayName ? null : filters.displayName[0],
                            email: !filters.email ? null : filters.email[0],
                            mobile: !filters.mobile ? null : filters.mobile[0],
                            roles: !filters.roles ? null : filters.roles,
                            lastSignedIn: !filters.lastSignedIn ? [] : filters.lastSignedIn,
                            userGroups: !filters.userGroups ? null : filters.userGroups,
                            actives: !filters.isActive ? [] : filters.isActive,
                        },
                    });

                default:
                    return undefined;
            }
        },
        [dispatch, companies]
    );

    const showSearchDropDown = useCallback(
        (props, dataIndex) =>
            SearchBox({
                filterDropDownProps: props,
                dataIndex,
                onRef: node => {
                    inputRef.current = node;
                },
            }),
        [inputRef]
    );

    const showFilterDropDown = useCallback(
        (props, filters) =>
            FilterBox({
                filterDropDownProps: props,
                filters,
            }),
        []
    );

    const onFilterDropdownVisibleChange = useCallback(
        visible => {
            if (visible) {
                setTimeout(() => inputRef?.current?.select(), 100);
            }
        },
        [inputRef]
    );

    const userCompanyList = dataSource.flatMap(user =>
        user.userCompanyList.flatMap(company => ({ text: company.displayName, value: company.id }))
    );

    const userCompanyFilterList = sortBy(
        'text',
        uniqWith((a, b) => a.value === b.value, userCompanyList)
    );

    const roles = dataSource.flatMap(user => user.roles.flatMap(role => ({ text: role.displayName, value: role.id })));

    const roleFilter = sortBy(
        'text',
        uniqWith((a, b) => a.value === b.value, roles)
    );

    const userGroups = dataSource.flatMap(user =>
        user.userGroups.flatMap(userGroup => ({ text: userGroup.displayName, value: userGroup.id }))
    );

    const userGroupsFilter = sortBy(
        'text',
        uniqWith((a, b) => a.value === b.value, userGroups)
    );

    useEffect(() => {
        dispatch({ type: 'setPage', page: 1 });
    }, [companies, dispatch]);

    const { emailSort, nameSort, mobileSort, lastSignSort } = useSortOrder(sort);

    const activeFilter = useActiveFilter();

    if (!loading) {
        if (error) {
            return <InternalErrorResult />;
        }
    }

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={(row: UserListDataFragment) => ({
                onClick: () => {
                    setCache(state);
                    navigate(`/admin/accesses/users/${row.id}`);
                },
                style: { cursor: 'pointer' },
            })}
            rowKey="id"
            scroll={{ x: true }}
            state={state}
            tableName={t('userList:title')}
            total={total}
        >
            {companies?.length > 1 && (
                <Table.Column
                    key="company"
                    dataIndex="userCompanyList"
                    filterDropdown={props => showFilterDropDown(props, userCompanyFilterList)}
                    filterIcon={renderFilterIcon}
                    filteredValue={filter.companyIds}
                    render={(value: UserListDataFragment['userCompanyList']) =>
                        value.map(company => <Tag key={company.id}>{company.displayName}</Tag>)
                    }
                    title={t('userList:columns.company')}
                />
            )}
            <Table.Column
                key="email"
                dataIndex="email"
                filterDropdown={props => showSearchDropDown(props, 'email')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.email ? [filter.email] : undefined}
                onFilterDropdownOpenChange={onFilterDropdownVisibleChange}
                sortOrder={emailSort}
                title={t('userList:columns.email')}
                sorter
            />
            <Table.Column
                key="displayName"
                dataIndex="displayName"
                filterDropdown={searchBox.render('displayName')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.displayName ? [filter.displayName] : undefined}
                onFilterDropdownOpenChange={searchBox.autoFocus}
                sortOrder={nameSort}
                title={t('userList:columns.displayName')}
                sorter
            />
            <Table.Column
                key="mobile"
                dataIndex={['mobile', 'value']}
                filterDropdown={props => showSearchDropDown(props, 'mobile')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.mobile ? [filter.mobile] : undefined}
                onFilterDropdownOpenChange={onFilterDropdownVisibleChange}
                sortOrder={mobileSort}
                title={t('userList:columns.mobile')}
                sorter
            />
            <Table.Column
                key="roles"
                dataIndex="roles"
                filterDropdown={props => showFilterDropDown(props, roleFilter)}
                filterIcon={renderFilterIcon}
                filteredValue={filter.roles}
                render={(value: UserListDataFragment['roles']) =>
                    value.map(role => <Tag key={role.id}>{role.displayName}</Tag>)
                }
                title={t('userList:columns.role')}
            />
            <Table.Column
                key="userGroups"
                dataIndex="userGroups"
                filterDropdown={props => showFilterDropDown(props, userGroupsFilter)}
                filterIcon={renderFilterIcon}
                filteredValue={filter.userGroups}
                render={(value: UserListDataFragment['userGroups']) =>
                    value.map(userGroup => <Tag key={userGroup.id}>{userGroup.displayName}</Tag>)
                }
                title={t('userList:columns.userGroups')}
            />
            <Table.Column
                key="lastSignedIn"
                dataIndex="lastSignedIn"
                filterDropdown={props => showFilterDropDown(props, dateFilterItems)}
                filterIcon={renderFilterIcon}
                filteredValue={filter.lastSignedIn}
                render={(value: UserListDataFragment['lastSignedIn'], record: UserListDataFragment) =>
                    !isNil(value)
                        ? formatDate({
                              date: value,
                              timeZone: company?.timeZone,
                              withOffset: true,
                          })
                        : ''
                }
                sortOrder={lastSignSort}
                title={t('userList:columns.lastSignedIn')}
                sorter
            />
            <Table.Column
                key="isActive"
                align="center"
                dataIndex="isActive"
                filterDropdown={props => showFilterDropDown(props, activeFilter)}
                filterIcon={renderFilterIcon}
                filteredValue={filter?.actives}
                onFilter={(value: boolean, record: UserListDataFragment) =>
                    (record.isActive && value) || (!record.isActive && !value)
                }
                render={value => renderBooleanIcon(value, true)}
                title={t('userList:columns.active')}
                width={120}
            />
        </PaginatedTableWithContext>
    );
};

export default UserList;
